import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { TrendingUp } from "lucide-react";
import CoachingTimelineDashboard from "./CoachingTimelineDashboard";
import SessionTimeDistributionChart from "./SessionTimeDistributionChart";

const SessionView = ({ sessions }) => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [recording, setRecording] = useState(null);

  useEffect(() => {
    if (sessions && sessionId) {
      const session = sessions.find((s) => s.recording_id === sessionId);
      console.log("Loading session:", {
        sessionId,
        found: !!session,
        sessionData: session
          ? {
              id: session.recording_id,
              space: session.space_name,
              hasTranscript: !!session.transcript?.transcript,
              hasInsights: !!(
                session.soft_skills_evaluation_2?.insights || session.insights
              ),
            }
          : null,
      });
      setRecording(session || null);
    }
  }, [sessionId, sessions]);

  const handleSessionChange = (newSessionId) => {
    const basePath = window.location.pathname.split("/session/")[0];
    navigate(`${basePath}/session/${newSessionId}`);
  };

  if (!recording) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <p className="text-gray-600">Session not found</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex-1"></div>
            <h1 className="text-4xl font-bold text-gray-800 flex-1">
              AI Coaching Dashboard
            </h1>
            <div className="flex-1 flex justify-end">
              <button
                type="button"
                onClick={() => {
                  const basePath =
                    window.location.pathname.split("/session/")[0];
                  navigate(`${basePath}/trendline`);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-[#2f80ed] text-white rounded-lg hover:bg-[#1e5bb8] transition-colors shadow-md"
              >
                <TrendingUp className="w-4 h-4" />
                <span className="font-medium">View Trendlines</span>
              </button>
            </div>
          </div>
          <p className="text-gray-600 text-lg">
            Comprehensive session monitoring and analysis
          </p>
        </div>

        {/* Session Selector */}
        {sessions && sessions.length > 1 && (
          <div className="mb-6">
            <div className="bg-white rounded-lg shadow-md p-4">
              <label
                htmlFor="session-select"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Select Recording
              </label>
              <select
                id="session-select"
                value={sessionId}
                onChange={(e) => handleSessionChange(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2f80ed] focus:border-transparent"
              >
                {sessions.map((session) => (
                  <option
                    key={session.recording_id}
                    value={session.recording_id}
                  >
                    {session.space_name} -{" "}
                    {session.recording_object?.startingTime
                      ? new Date(
                          session.recording_object.startingTime
                        ).toLocaleDateString()
                      : "N/A"}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Session Time Distribution Chart */}
        <div className="mb-8">
          <SessionTimeDistributionChart
            key={`session-chart-${recording.recording_id}`}
            recording={recording}
          />
        </div>

        {/* Coaching Timeline Dashboard */}
        <CoachingTimelineDashboard
          key={`dashboard-${recording.recording_id}`}
          recording={recording}
        />
      </div>
    </div>
  );
};

export default SessionView;
